import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Calendar, Clock, Users, Star, Piano, Drum, Speaker, Mic, Music } from 'lucide-react';
import Header from './Header';

const RoomDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [duration, setDuration] = useState(2);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('');

  // Sample room data - in a real app, this would come from an API
  const room = {
    id: parseInt(id),
    name: "Studio A - The Cathedral",
    size: "band",
    capacity: "5-8 people",
    price: 45,
    rating: 4.8,
    images: [
      "https://mockimg.co/800x400",
      "https://mockimg.co/800x400",
      "https://mockimg.co/800x400",
      "https://mockimg.co/800x400"
    ],
    facilities: ["piano", "drums", "amps", "recording", "ac"],
    available: ["9:00", "13:00", "18:00", "21:00"],
    description: "Professional recording studio with high-end equipment. This spacious room features state-of-the-art recording equipment, multiple isolation booths, and a comfortable control room. Perfect for bands looking to record their next album or rehearse in a professional environment.",
    longDescription: "The Cathedral is our flagship recording studio, designed for professional musicians and bands. With over 500 square feet of space, this room offers the perfect balance of acoustics and comfort. The main room features high ceilings and natural light, creating an inspiring environment for creativity. The control room is equipped with the latest recording technology, including a 48-channel mixing console and high-end monitors. Multiple isolation booths allow for separate recording of different instruments, while the main room can accommodate a full band setup. The room is also equipped with a variety of vintage and modern microphones, amplifiers, and instruments available for use.",
    dimensions: "25' x 20'",
    equipment: [
      "48-channel mixing console",
      "High-end studio monitors",
      "Multiple isolation booths",
      "Vintage microphones collection",
      "Professional drum kit",
      "Grand piano",
      "Multiple guitar amplifiers",
      "Bass amplifiers",
      "PA system"
    ],
    rules: [
      "No food or drinks in the studio",
      "No smoking",
      "Maximum 8 people",
      "Must be 18 or older",
      "Equipment must be handled with care",
      "Studio must be left clean"
    ]
  };

  const facilityIcons = {
    piano: <Piano size={16} />,
    drums: <Drum size={16} />,
    amps: <Speaker size={16} />,
    mics: <Mic size={16} />,
    recording: <Music size={16} />,
    pa: <Speaker size={16} />,
    ac: "❄️",
    stage: "🎭"
  };

  const facilityNames = {
    piano: "Piano",
    drums: "Drum Kit",
    amps: "Guitar Amps",
    mics: "Microphones",
    recording: "Recording",
    pa: "PA System",
    ac: "Air Con",
    stage: "Stage"
  };

  const getSizeColor = (size) => {
    switch(size) {
      case 'solo': return 'bg-blue-100 text-blue-800';
      case 'band': return 'bg-green-100 text-green-800';
      case 'ensemble': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const sizeLabels = {
    solo: "Solo",
    band: "Band",
    ensemble: "Ensemble"
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % room.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + room.images.length) % room.images.length);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
      <Header />

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Rooms
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Room Header */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8 border dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{room.name}</h1>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSizeColor(room.size)}`}>
                      {sizeLabels[room.size]}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300 flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {room.capacity}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300 flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 mr-1" />
                      {room.rating}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">${room.price}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">per hour</p>
                </div>
              </div>
            </div>

            {/* Image Carousel */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden mb-8 border dark:border-gray-700">
              <div className="relative h-96">
                <img
                  src={room.images[currentImageIndex]}
                  alt={`${room.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 p-2 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-colors"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 p-2 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-colors"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                  {room.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex
                          ? 'bg-white'
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Room Description */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8 border dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">About This Room</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">{room.longDescription}</p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dimensions</h3>
                  <p className="text-gray-600 dark:text-gray-400">{room.dimensions}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Equipment</h3>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                    {room.equipment.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">House Rules</h3>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                  {room.rules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Facilities */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8 border dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Facilities</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {room.facilities.map(facility => (
                  <div
                    key={facility}
                    className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <span className="mr-2">{facilityIcons[facility]}</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{facilityNames[facility]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking Card */}
          <div className="w-96 flex-shrink-0">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border dark:border-gray-700 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Book This Room</h2>

              {/* Date Picker */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formatDate(selectedDate)}</p>
              </div>

              {/* Time Picker */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Time</label>
                <select
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a time</option>
                  {room.available.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>

              {/* Duration */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Duration</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value={1}>1 hour</option>
                  <option value={2}>2 hours</option>
                  <option value={3}>3 hours</option>
                  <option value={4}>4 hours</option>
                  <option value={6}>6 hours</option>
                  <option value={8}>8 hours</option>
                </select>
              </div>

              {/* Price Summary */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600 dark:text-gray-300">Price per hour</span>
                  <span className="text-gray-900 dark:text-white">${room.price}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600 dark:text-gray-300">Duration</span>
                  <span className="text-gray-900 dark:text-white">{duration} hours</span>
                </div>
                <div className="flex justify-between font-semibold text-lg">
                  <span className="text-gray-900 dark:text-white">Total</span>
                  <span className="text-indigo-600 dark:text-indigo-400">${room.price * duration}</span>
                </div>
              </div>

              {/* Book Button */}
              <button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                disabled={!selectedTime}
              >
                <Calendar className="h-5 w-5 mr-2" />
                Book Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetail;